import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, Eye, Layers } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ROUTE_PATHS } from '@/lib/index';
import { IMAGES } from '@/assets/images';
import { fadeInUp } from '@/lib/motion';

interface ArchitecturalProduct {
  id: string;
  title: string;
  description: string;
  image: string | null;
  features: string[];
  applications: string[];
  subcategories?: { title: string; image: string }[];
}

const architecturalProducts: ArchitecturalProduct[] = [
  {
    id: 'standart-profiller',
    title: 'Standart Profiller',
    description: 'Geniş uygulama yelpazesi için üretilen, standart kesitli alüminyum profil serileri.',
    image: null,
    features: [
      'Press (pres) ile üretim',
      'Stok programı mevcudu',
      'Hızlı temin imkanı',
      'Geniş kesit yelpazesi',
      'Çeşitli alaşım seçenekleri',
      'Ekonomik çözümler'
    ],
    applications: [
      'Genel inşaat uygulamaları',
      'Endüstriyel kullanım',
      'OEM üreticiler',
      'Proje uygulamaları'
    ]
  },
  {
    id: 'architectural-detail-profiles',
    title: 'Mimari Detay Çözüm Profilleri',
    description: 'Özel mimari detaylar için tasarlanmış, yüksek hassasiyetli profil çözümleri.',
    image: IMAGES.PROFIL_DEKO_288,
    features: [
      'Özel tasarım imkanı',
      'Yüksek hassasiyet',
      'Çeşitli yüzey işlemleri',
      'Kompleks geometriler',
      'Mimar onaylı çözümler',
      'Proje bazlı üretim'
    ],
    subcategories: [
      { title: 'Dış Köşe Seramik Profilleri', image: IMAGES.A_291 },
      { title: 'Alüminyum Köşe Profilleri', image: IMAGES.B_289 },
      { title: 'Alüminyum Zemin Bitim Profilleri', image: IMAGES.C_292 },
      { title: 'Alüminyum Basamak Profilleri', image: IMAGES.D_290 },
      { title: 'Alüminyum Balkon Profilleri', image: IMAGES.F_298 },
      { title: 'Alüminyum Iç Köşe Profilleri', image: IMAGES.G_295 },
      { title: 'Alüminyum Eşik Profilleri', image: IMAGES.H_294 },
      { title: 'Alüminyum Merdiven Basamak Profilleri', image: IMAGES.I_296 },
      { title: 'Alüminyum Duşakabin Profilleri', image: IMAGES.J_297 }
    ],
    applications: [
      'Özel mimari projeler',
      'Restorasyon çalışmaları',
      'Sanat eserleri',
      'Tasarım objeleri'
    ]
  },
  {
    id: 'lighting-profiles',
    title: 'Aydınlatma Profilleri',
    description: 'Modern aydınlatma çözümleri için estetik ve fonksiyonel LED profil sistemleri.',
    image: IMAGES.LED_PROFILLERI_YEPYENI_287,
    features: [
      'LED şerit uyumlu tasarım',
      'İşık dağılımı optimizasyonu',
      'Kolay montaj sistemi',
      'Termal yönetim',
      'Çeşitli boyut seçenekleri',
      'Uzun ömürlü yapı'
    ],
    applications: [
      'İç mekan aydınlatması',
      'Vitrin aydınlatması',
      'Dekoratif aydınlatma',
      'Mimari vurgulama'
    ]
  },
  {
    id: 'store-perde',
    title: 'Store Perde Profilleri',
    description: 'Güneş kontrolü ve gizlilik için tasarlanmış, yüksek performanslı store perde sistemleri.',
    image: IMAGES.PERDE_PROFILI,
    features: [
      '4" ve 5" ekstrüzyon hattı kapasitesi',
      'Boyalı (9016) ve bypass seçenekleri',
      'Tam güneş kontrolü',
      'Esnek tasarım seçenekleri',
      'Sessiz çalışma mekanizması',
      'Uzun ömürlü yapı'
    ],
    applications: [
      'Konut pencereleri',
      'Ofis binaları',
      'Oteller',
      'Ticari yapılar'
    ]
  },
  {
    id: 'curtain-profiles',
    title: 'Perde Profilleri',
    description: 'Modern iç mekan tasarımı için estetik perde ray ve profil sistemleri.',
    image: IMAGES.PERDE_PROFILLERI_281,
    features: [
      'Sessiz kayma mekanizması',
      'Çeşitli montaj seçenekleri',
      'Dayanıklı alaşım yapısı',
      'Estetik görünüm',
      'Kolay kurulum',
      'Uzun kullanım ömrü'
    ],
    applications: [
      'Konut iç mekanları',
      'Ofis binaları',
      'Otel odaları',
      'Ticari mekanlar'
    ]
  },
  {
    id: 'mobilya-profilleri',
    title: 'Mobilya Profilleri',
    description: 'Mobilya üretiminde kullanılan, estetik ve dayanıklı alüminyum profil çözümleri.',
    image: IMAGES.MOBILYA_PROFILI,
    features: [
      '4" ve 5" ekstrüzyon hattı kapasitesi',
      'Boyalı ve eloksal yüzey seçenekleri',
      'Özel kesit tasarımı',
      'Yüksek yüzey kalitesi',
      'Geniş renk paleti',
      'Proje bazlı üretim'
    ],
    applications: [
      'Mutfak mobilyaları',
      'Ofis mobilyaları',
      'Otel mobilyaları',
      'Özel tasarım mobilyalar'
    ]
  },
  {
    id: 'dusaKabin-profilleri',
    title: 'Duşakabin Profilleri',
    description: 'J-tipi ve özel kesitli, hijyenik ve estetik duşakabin çerçeve profilleri.',
    image: IMAGES.BANYO_KABINI,
    features: [
      'J-tipi özel kesit tasarımı',
      '4" ve 5" ekstrüzyon hattı kapasitesi',
      'Boyalı ve eloksal yüzey seçenekleri',
      'Su sızdırmazlık',
      'Kolay temizlenebilir yüzey',
      'Cam tutma sistemleri'
    ],
    applications: [
      'Konut banyoları',
      'Otel banyoları',
      'SPA ve wellness alanları',
      'Spor tesisleri'
    ]
  },
  {
    id: 'screen-systems',
    title: 'Sineklik Sistemleri',
    description: 'Konfor ve hijyen sağlayan, estetik sineklik ve koruma sistemleri.',
    image: IMAGES.SINEKLIK_301,
    features: [
      'Mikro delikli file yapısı',
      'UV dayanımlı malzeme',
      'Kolay montaj ve söküm',
      'Hava akışını engellemez',
      'Çeşitli renk seçenekleri',
      'Uzun ömürlü yapı'
    ],
    applications: [
      'Konut pencereleri',
      'Balkon kapıları',
      'Teras alanı',
      'Ofis binaları'
    ]
  },
  {
    id: 'pergola-systems',
    title: 'Pergola Sistemleri',
    description: 'Dış mekan yaşam alanları için estetik ve fonksiyonel pergola çözümleri.',
    image: IMAGES.PERGOLA_1_279,
    features: [
      'Ayarlanabilir lamelli tavan',
      'Su geçirmez yapı',
      'Rüzgar dayanımı',
      'LED aydınlatma entegrasyonu',
      'Uzaktan kumanda kontrol',
      'Modüler tasarım'
    ],
    applications: [
      'Bahçe ve teraslar',
      'Restoran dış alanları',
      'Otel havuz kenarları',
      'Konut dış mekanları'
    ]
  },
  {
    id: 'bioclimatic-pergola',
    title: 'Bioklimatik Pergola',
    description: 'İklim kontrollü sağlayan, akıllı pergola sistemleri.',
    image: IMAGES.BIOKLIMATIK_280,
    features: [
      'Otomatik iklim kontrolü',
      'Sensör tabanlı çalışma',
      'Enerji verimli tasarım',
      'Akıllı ev entegrasyonu',
      'Hava durumu adaptasyonu',
      'Sessiz motor teknolojisi'
    ],
    applications: [
      'Lüks konut projeleri',
      'Boutique oteller',
      'Premium restoranlar',
      'Özel villa bahçeleri'
    ]
  },
  {
    id: 'minimal-sliding-systems',
    title: 'Minimal Sürme Sistemleri',
    description: 'Modern minimalist tasarım anlayışıyla üretilen, ince profilli sürme sistemleri.',
    image: IMAGES.MINIMAL_SURME_SISTEMLER_6_299,
    features: [
      'Ultra ince profil tasarımı',
      'Maksimum cam alanı',
      'Sessiz sürme mekanizması',
      'Yüksek yalıtım performansı',
      'Estetik görünüm',
      'Kolay kullanım'
    ],
    applications: [
      'Modern konutlar',
      'Minimalist ofisler',
      'Sanat galerileri',
      'Tasarım stüdyoları'
    ]
  },
  {
    id: 'office-partition-systems',
    title: 'Ofis Bölme Sistemleri',
    description: 'Esnek çalışma alanları için modüler ofis bölme ve duvar sistemleri.',
    image: IMAGES.OFFICE_PART_300,
    features: [
      'Modüler sistem tasarımı',
      'Hızlı montaj ve demontaj',
      'Ses yalıtımı',
      'Cam ve panel seçenekleri',
      'Kablo geçiş imkanı',
      'Çeşitli yükseklik seçenekleri'
    ],
    applications: [
      'Ofis binaları',
      'Coworking alanları',
      'Banka şubeleri',
      'Hastane bölmeleri'
    ]
  },
  {
    id: 'reklam-profilleri',
    title: 'Reklam Profilleri',
    description: 'Tabela, reklam panosu ve görsel iletişim sistemleri için mat eloksal yüzeyli profil çözümleri.',
    image: IMAGES.REKLAM_ICIN,
    features: [
      'Mat eloksal yüzey işlemi',
      'Yüksek estetik görünüm',
      'Işık yayma özellikleri',
      'Kolay baskı uyumu',
      'Uzun ömürlü yüzey',
      'Çeşitli kesit seçenekleri'
    ],
    applications: [
      'Reklam tabelaları',
      'Işıklı panolar',
      'Mağaza tabelaları',
      'Yönlendirme sistemleri'
    ]
  },
  {
    id: 'dorse-profilleri',
    title: 'Dorse Profilleri',
    description: 'Taşımacılık ve ulaştırma sektörüne yönelik, yüksek mukavemetli dorse kasası profilleri.',
    image: IMAGES.TENTELI_DORSE,
    features: [
      '7" ekstrüzyon hattı kapasitesi',
      'Yüksek mukavemet alaşım',
      'Hafif ve sağlam yapı',
      'Korozyon direnci',
      'Uzun ömürlü kullanım',
      'Özel kesit tasarımı'
    ],
    applications: [
      'Dorse kasaları',
      'Yük taşıma araçları',
      'Soğuk zincir araçları',
      'Özel taşıma ekipmanları'
    ]
  },
  {
    id: 'marine-yacht-profiles',
    title: 'Marine (Yat) Profilleri',
    description: 'Denizcilik ve yat uygulamaları için özel olarak geliştirilen, korozyona dayanıklı ve hafif alüminyum profil sistemleri.',
    image: IMAGES.YACHT_MARINE_2,
    features: [
      'Denizcilik sınıfı alaşım (5000 & 6000 serisi)',
      'Üstün tuzlu su korozyon direnci',
      'Hafif ve yüksek mukavemetli yapı',
      'Anodize ve özel kaplama seçenekleri',
      'Özel kesit tasarımı',
      'Zorlu deniz koşullarında uzun ömürlü kullanım'
    ],
    applications: [
      'Yat ve tekne gövdeleri',
      'Güverte ve üst yapı elemanları',
      'Deniz korkulukları ve trabzanları',
      'Liman ve marina yapıları'
    ]
  }
];

export default function ArchitecturalSystems() {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-24 bg-primary overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={IMAGES.ALUMINUM_PRODUCTS_1} 
            alt="Mimari Sistemler" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-primary/60" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <Link 
              to={ROUTE_PATHS.HOME} 
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Ana Sayfaya Dön
            </Link>
            
            <h1 className="text-5xl font-bold text-white mb-6">
              Mimari Sistemler
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Modern yapılar için estetik, dayanıklı ve yüksek yalıtımlı alüminyum çözümleri. 
              Enerji verimliliği ve tasarım mükemmelliğini bir araya getiren sistemlerimiz.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="space-y-16">
            {architecturalProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.6 }}
              >
                {product.image ? (
                  /* Fotoğraflı ürün - 2 kolonlu layout */
                  <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                    index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                  }`}>
                    <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                      <div className="relative rounded-2xl overflow-hidden group">
                        <img
                          src={product.image}
                          alt={product.title}
                          className="w-full h-[400px] object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      </div>
                    </div>
                    <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                      <h2 className="text-3xl font-bold text-primary mb-4">{product.title}</h2>
                      <p className="text-muted-foreground mb-6 text-lg">{product.description}</p>
                      <div className="mb-8">
                        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                          <Shield className="w-5 h-5 text-primary" /> Teknik Özellikler
                        </h3>
                        <ul className="space-y-2">
                          {product.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                              <span className="text-muted-foreground">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      {product.subcategories && (
                        <div className="mb-8">
                          <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                            <Layers className="w-5 h-5 text-primary" /> Profil Kategorileri
                          </h3>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                            {product.subcategories.map((category, idx) => (
                              <div key={idx} className="group cursor-pointer">
                                <div className="relative rounded-lg overflow-hidden mb-2 aspect-square">
                                  <img src={category.image} alt={category.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" />
                                </div>
                                <h4 className="text-xs font-medium text-center text-foreground leading-tight">{category.title}</h4>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="mb-8">
                        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                          <Eye className="w-5 h-5 text-primary" /> Uygulama Alanları
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {product.applications.map((app, idx) => (
                            <span key={idx} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">{app}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Fotoğrafsız ürün - tam genişlik kart layout */
                  <div className="border border-border rounded-2xl p-8 bg-card hover:shadow-lg transition-shadow">
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Layers className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-primary mb-2">{product.title}</h2>
                        <p className="text-muted-foreground">{product.description}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                          <Shield className="w-5 h-5 text-primary" /> Teknik Özellikler
                        </h3>
                        <ul className="space-y-2">
                          {product.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                              <span className="text-muted-foreground text-sm">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                          <Eye className="w-5 h-5 text-primary" /> Uygulama Alanları
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {product.applications.map((app, idx) => (
                            <span key={idx} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">{app}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl font-bold text-primary mb-6">
              Projeniz İçin Özel Çözüm mü Arıyorsunuz?
            </h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Uzman mühendislik ekibimiz, mimari sistemler konusunda size özel çözümler geliştirir. 
              Projenizdeki teknik gereksinimleri birlikte değerlendirelim.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="px-8">
                <Link to={`${ROUTE_PATHS.CONTACT}#teklif-formu`}>
                  Teklif İsteyin
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="px-8">
                <Link to={ROUTE_PATHS.PRODUCTS}>
                  Tüm Ürünleri Görün
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}